// Dynamic favicon badge — overlays a red "N" bubble on the app icon (like
// native app badges) to surface the count of new/unseen cards on the browser
// tab. Falls back to the plain favicon when the count is 0.

const BASE_HREF = '/favicon.svg'
const SIZE = 64 // canvas render size (browsers downscale to the tab icon)

let baseImg: HTMLImageElement | null = null
let baseReady = false
let pending: number | null = null

function getLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  return link
}

function loadBase(): void {
  if (baseImg) return
  baseImg = new Image()
  baseImg.onload = () => {
    baseReady = true
    if (pending != null) {
      const n = pending
      pending = null
      setFaviconBadge(n)
    }
  }
  baseImg.src = BASE_HREF
}

/** Set (or clear) the numeric badge on the favicon. count <= 0 restores the
 *  plain icon. */
export function setFaviconBadge(count: number): void {
  const link = getLink()

  if (count <= 0) {
    link.type = 'image/svg+xml'
    link.href = BASE_HREF
    return
  }

  if (!baseReady) {
    pending = count
    loadBase()
    return
  }

  const canvas = document.createElement('canvas')
  canvas.width = SIZE
  canvas.height = SIZE
  const ctx = canvas.getContext('2d')
  if (!ctx || !baseImg) return

  ctx.clearRect(0, 0, SIZE, SIZE)
  ctx.drawImage(baseImg, 0, 0, SIZE, SIZE)

  const label = count > 99 ? '99+' : String(count)
  // Badge geometry — top-right corner (convention), slightly overflowing;
  // scales a touch for 2–3 digit counts so it stays a round pill.
  const r = label.length > 1 ? 19 : 17
  const cx = SIZE - r - 1
  const cy = r + 1

  // White ring so the red bubble stays legible over any icon edge.
  ctx.beginPath()
  ctx.arc(cx, cy, r + 2.5, 0, Math.PI * 2)
  ctx.fillStyle = '#ffffff'
  ctx.fill()

  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fillStyle = '#ef4444' // red-500
  ctx.fill()

  // White numerals on red — max contrast, most legible at favicon size.
  ctx.fillStyle = '#ffffff'
  ctx.font = `800 ${label.length > 2 ? 24 : 30}px -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(label, cx, cy + 1)

  link.type = 'image/png'
  link.href = canvas.toDataURL('image/png')
}
